import { Button, Stack } from "@mui/material";
import type { GridRowHeightParams, GridSortModel } from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router";

import {
  DataGridHeader,
  useDataGridHeaderScrollSync,
} from "~/components/UI/DataGridHeader";
import { dataGridSettings } from "~/constants/columnDataGridSettings";
import { InitialMarketsDataGrid } from "~/feature/event/components/InitialMarketsDataGrid";
import {
  DEFAULT_MARKETS_SORT_MODEL,
  GROUPED_ROW_HEIGHT,
  INITIAL_MARKETS_LIMIT,
  marketsDataGridSettings,
} from "~/feature/event/components/MarketsDataGrid/constants";
import { backendSortParamsFromMarketsSortModel } from "~/feature/event/components/MarketsDataGrid/marketsGridSortToBackendSort";
import {
  type IMarketsDataGridRow,
  isMarketsDataGridMarketRow,
  isMarketsDataGridNoteRow,
  MarketsDataGridField,
} from "~/feature/event/components/MarketsDataGrid/types";
import { useMarketsDataGridChartFocus } from "~/feature/event/components/MarketsDataGrid/useMarketsDataGridChartFocus";
import { useMarketsDataGridColumns } from "~/feature/event/components/MarketsDataGrid/useMarketsDataGridColumns";
import {
  useHasMarketsDataGridFilters,
  useMarketsDataGridFilters,
} from "~/feature/event/components/MarketsDataGrid/useMarketsDataGridFilters";
import { useMarketsDataGridFooterCount } from "~/feature/event/components/MarketsDataGrid/useMarketsDataGridFooterCount";
import { useMarketsDataGridHeader } from "~/feature/event/components/MarketsDataGrid/useMarketsDataGridHeader";
import { useMarketsDataGridHeaderPin } from "~/feature/event/components/MarketsDataGrid/useMarketsDataGridHeaderPin";
import { useMarketsDataGridRows } from "~/feature/event/components/MarketsDataGrid/useMarketsDataGridRows";
import {
  DEFAULT_MARKETS_SORT_PARAMS,
  EventPageSections,
  MARKETS_GROUP_RANK_PARAMS,
} from "~/feature/event/constants";
import { useEventLoaderData } from "~/feature/event/hooks/useEventLoaderData";
import { useSeedEventMarkets } from "~/feature/event/hooks/useSeedEventMarkets";
import {
  useMarketCalculator,
  useMarketCalculatorActions,
} from "~/feature/event/providers/MarketCalculatorProvider";
import { useEventMarkets } from "~/feature/event/useEventMarkets";
import { getCalculatorMarketGroup } from "~/feature/event/utils/getCalculatorMarketGroup";
import { useIsClient } from "~/hooks/useIsClient";
import { useScreenResolution } from "~/hooks/useScreenResolution";
import { useFilter } from "~/providers/FilterProvider";
import type {
  IAggregatedEvent,
  IMarket,
  IOutcomeConsensus,
} from "~/types/event";
import { runAfterLayout } from "~/utils/runAfterLayout";
import { scrollWindowToElement } from "~/utils/scrollWindowToElement";

import * as Styled from "./MarketsDataGrid.styles";

interface IMarketsDataGridProps {
  event: IAggregatedEvent;
}

const SHOW_MORE_SCROLL_OFFSET = 42;
// Stable identity, so the seeding effect does not re-run on every render when
// the loader has no markets.
const EMPTY_MARKETS: IMarket[] = [];
const EMPTY_OUTCOMES: IOutcomeConsensus[] = [];

export const MarketsDataGrid: FC<IMarketsDataGridProps> = ({ event }) => {
  const isClient = useIsClient();
  const loaderData = useEventLoaderData();
  const initialMarkets = loaderData?.markets ?? EMPTY_MARKETS;
  const initialOutcomes = loaderData?.outcomes ?? EMPTY_OUTCOMES;

  useSeedEventMarkets(event.id, initialMarkets, initialOutcomes);

  const { hash } = useLocation();
  const [searchParams] = useSearchParams();
  const marketIdFromUrl = searchParams.get("market");
  const shouldLoadAllForDeepLink = Boolean(marketIdFromUrl);
  const { isMobile } = useScreenResolution();
  const { setIsCalculatorVisible, market } = useMarketCalculator();
  const focusChartMarket = useMarketsDataGridChartFocus();
  const { setIsDataLoading } = useFilter();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const scrollToRowIndexRef = useRef<number | null>(null);

  const [sortModel, setSortModel] = useState<GridSortModel>(
    DEFAULT_MARKETS_SORT_MODEL
  );
  const shouldShowAllByHash = hash === `#${EventPageSections.MarketsTabs}`;
  const [showAll, setShowAll] = useState(shouldShowAllByHash);
  const { setMarket, setGroupedMarkets } = useMarketCalculatorActions();

  const { columns } = useMarketsDataGridColumns({ event });

  const backendFilterParams = useMarketsDataGridFilters();
  const hasActiveMarketFilters = useHasMarketsDataGridFilters();

  useEffect(() => {
    if (!shouldShowAllByHash) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setShowAll(true);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [shouldShowAllByHash]);

  const backendSortParams = useMemo(
    () => backendSortParamsFromMarketsSortModel(sortModel),
    [sortModel]
  );

  const requestParams = useMemo(() => {
    const withFilters = { ...backendFilterParams };

    if (backendSortParams.sortBy) {
      return { ...withFilters, ...backendSortParams };
    }

    return withFilters;
  }, [backendFilterParams, backendSortParams]);

  const isDefaultSort =
    backendSortParams.sortBy === DEFAULT_MARKETS_SORT_PARAMS.sortBy &&
    backendSortParams.sortOrder === DEFAULT_MARKETS_SORT_PARAMS.sortOrder;

  const shouldLoadAllMarkets =
    isDefaultSort && !backendFilterParams.ungroup && !hasActiveMarketFilters;

  const { data: rawData, isLoading } = useEventMarkets(
    shouldLoadAllMarkets
      ? {
          id: event.id,
          all: true,
          ...MARKETS_GROUP_RANK_PARAMS,
          ...DEFAULT_MARKETS_SORT_PARAMS,
        }
      : {
          id: event.id,
          ...(showAll || shouldLoadAllForDeepLink
            ? { all: true }
            : { all: false, page: 1, limit: INITIAL_MARKETS_LIMIT }),
          ...MARKETS_GROUP_RANK_PARAMS,
          ...requestParams,
        }
  );

  useEffect(() => {
    setIsDataLoading(isLoading);
  }, [isLoading, setIsDataLoading]);

  const data = useMemo(() => {
    if (
      !shouldLoadAllMarkets ||
      showAll ||
      shouldLoadAllForDeepLink ||
      !rawData
    ) {
      return rawData;
    }

    return {
      ...rawData,
      total: rawData.data.length,
      data: rawData.data.slice(0, INITIAL_MARKETS_LIMIT),
    };
  }, [shouldLoadAllMarkets, showAll, shouldLoadAllForDeepLink, rawData]);

  const rows = useMarketsDataGridRows({
    markets: data?.data ?? [],
    outcomes: data?.outcomes ?? EMPTY_OUTCOMES,
    isUngrouped: Boolean(backendFilterParams.ungroup),
  });

  const applyCalculatorMarketGroup = useCallback(
    (selectedMarket: IMarket) => {
      const pool = rawData?.data ?? [];
      const { market: resolvedMarket, groupedMarkets } =
        getCalculatorMarketGroup(selectedMarket, pool);

      setMarket(resolvedMarket);
      setGroupedMarkets(groupedMarkets);
    },
    [rawData, setGroupedMarkets, setMarket]
  );

  useEffect(() => {
    if (!data?.data?.length) return;

    if (market) {
      if (data.data.some((item) => item.id === market.id)) {
        applyCalculatorMarketGroup(market);
        return;
      }

      if (marketIdFromUrl === market.id) {
        applyCalculatorMarketGroup(market);
        return;
      }
    }

    applyCalculatorMarketGroup(data.data[0]);
  }, [
    applyCalculatorMarketGroup,
    backendFilterParams.ungroup,
    data,
    market,
    marketIdFromUrl,
  ]);

  const handleOnRowClick = useCallback(
    (row: IMarketsDataGridRow) => {
      if (!isMarketsDataGridMarketRow(row)) {
        return;
      }

      if (isMobile) {
        setIsCalculatorVisible(false);
      }

      applyCalculatorMarketGroup(row);
      focusChartMarket(row.id);
    },
    [
      applyCalculatorMarketGroup,
      focusChartMarket,
      isMobile,
      setIsCalculatorVisible,
    ]
  );

  const isEveryRow = useMemo(() => {
    return rows.every((item) => item.rowType === "market");
  }, [rows]);

  const getRowClassName = useCallback(
    (row: IMarketsDataGridRow) => {
      if (
        backendFilterParams.ungroup ||
        !(event.eventCount > 1) ||
        isEveryRow
      ) {
        return "";
      }

      if (isMarketsDataGridNoteRow(row)) {
        return "grouped note";
      }

      if (!isMarketsDataGridMarketRow(row)) {
        return "grouped";
      }

      return row.isInGroup ? "" : "not-grouped-market";
    },
    [backendFilterParams.ungroup, event.eventCount, isEveryRow]
  );

  const getRowHeight = useCallback(({ model }: GridRowHeightParams) => {
    const row = model as IMarketsDataGridRow;

    if (isMarketsDataGridNoteRow(row)) {
      return "auto";
    }

    if (!isMarketsDataGridMarketRow(row)) {
      return GROUPED_ROW_HEIGHT;
    }

    return null;
  }, []);

  const total = data?.total ?? 0;
  const {
    totalMarketRowsCount,
    shouldShowToggleButton,
    localeText,
    slots,
    slotProps,
  } = useMarketsDataGridFooterCount({
    rows,
    total,
    isAllMarketsLoaded: showAll,
    initialRowsLimit: INITIAL_MARKETS_LIMIT,
  });
  const isMarketPinned = useMarketsDataGridHeaderPin(
    rootRef,
    isLoading,
    rows.length
  );

  const { items: headerItems, handleSortChange: handleHeaderSortChange } =
    useMarketsDataGridHeader({
      columns,
      sortModel,
      onSortModelChange: setSortModel,
      isGrouped: event.eventCount > 1,
      isMobile,
      isMarketPinned,
    });

  useDataGridHeaderScrollSync(rootRef, headerRef, isLoading);

  const handleShowAllToggle = useCallback(() => {
    setShowAll((prev) => {
      if (!prev) {
        scrollToRowIndexRef.current = rows.length;
      }

      return !prev;
    });
  }, [rows.length]);

  useEffect(() => {
    const targetRowIndex = scrollToRowIndexRef.current;

    if (targetRowIndex === null || !showAll || isLoading) {
      return;
    }

    const targetRow = rows[targetRowIndex];

    if (!targetRow) {
      if (!isLoading) {
        scrollToRowIndexRef.current = null;
      }

      return;
    }

    return runAfterLayout(() => {
      const rowElement = rootRef.current?.querySelector(
        `[data-id="${targetRow.id}"]`
      );

      if (rowElement) {
        scrollWindowToElement(rowElement, SHOW_MORE_SCROLL_OFFSET);
      }

      scrollToRowIndexRef.current = null;
    });
  }, [isLoading, rows, showAll]);

  if (initialMarkets.length > 0 && (!isClient || (!data && isLoading))) {
    return (
      <Styled.Root>
        <InitialMarketsDataGrid
          event={event}
          markets={initialMarkets}
          outcomes={initialOutcomes}
          limit={showAll ? initialMarkets.length : INITIAL_MARKETS_LIMIT}
          headerItems={headerItems}
        />
      </Styled.Root>
    );
  }

  return (
    <Styled.Root>
      <DataGridHeader
        rootRef={headerRef}
        items={headerItems}
        onSortChange={handleHeaderSortChange}
      />

      <DataGridPro<IMarketsDataGridRow>
        autoHeight
        ref={rootRef}
        columns={columns}
        rows={rows}
        rowCount={totalMarketRowsCount}
        loading={isLoading}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        onRowClick={({ row }) => handleOnRowClick(row)}
        getRowHeight={getRowHeight}
        getRowClassName={({ row }) => getRowClassName(row)}
        isRowSelectable={({ row }) => isMarketsDataGridMarketRow(row)}
        rowSelectionModel={{
          type: "include",
          ids: new Set(market?.id ? [market.id] : []),
        }}
        onRowSelectionModelChange={() => {}}
        columnHeaderHeight={1}
        hideFooterSelectedRowCount
        pinnedColumns={{
          left: [MarketsDataGridField.Market],
        }}
        {...dataGridSettings}
        {...marketsDataGridSettings}
        localeText={localeText}
        slots={slots}
        slotProps={slotProps}
      />

      {shouldShowToggleButton && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={handleShowAllToggle}
            disabled={isLoading}
            color="info"
          >
            {showAll ? "Show less" : "Show more"}
          </Button>
        </Stack>
      )}
    </Styled.Root>
  );
};
